import React from 'react';
import { Measure as MeasureType } from '../../types';
import StyledChord from '../StyledChord';
import { XDivider, DiagonalDivider, getIncenterPosition } from './GridDividers';

export interface GigMeasureContentProps {
  measure: MeasureType;
  fontSize: number;
  onChordClick: (measureIndex: number, chordIndex: number) => void;
  onChordHover: (chord: string, element: HTMLElement) => void;
  onChordLeave: () => void;
  measureIndex: number;
  invalidChords: Record<string, string | null>;
  gigChordScale?: number; // user scale multiplier (D), default 1.0
}

// ─── Option C : fitting dynamique par mesure ─────────────────────────────
// Réduit le fontSize selon le chord le plus long dans la mesure pour éviter
// les débordements, puis applique le multiplicateur utilisateur (gigChordScale).
const CHORD_LENGTH_THRESHOLDS: Array<{ maxLen: number; ratio: number }> = [
  { maxLen: 3,  ratio: 1.0  }, // "C", "Am"         → pleine taille
  { maxLen: 5,  ratio: 0.90 }, // "Cmaj7"           → 90%
  { maxLen: 7,  ratio: 0.78 }, // "Cmaj7/G"         → 78%
  { maxLen: 9,  ratio: 0.68 }, // "Cmaj13#11"       → 68%
  { maxLen: 999, ratio: 0.58 }, // plus long          → 58%
];

// Fraction of the 1-chord base font each subdivision gets.
// Derived from SUBDIVISION_WIDTH_RATIOS in gigAutoScale: 0.44/0.82 ≈ 0.54, 0.38/0.82 ≈ 0.46
const SUBDIVISION_RATIOS = [1.0, 0.54, 0.46, 0.46];

function getMeasureFitRatio(chords: (string | null)[]): number {
  const maxLen = Math.max(...chords.map(c => (c && c !== '/' && c !== '%' && c !== 'x') ? c.length : 0), 0);
  const charRatio = CHORD_LENGTH_THRESHOLDS.find(t => maxLen <= t.maxLen)!.ratio;
  const subRatio = SUBDIVISION_RATIOS[Math.min(chords.length, 4) - 1];
  return charRatio * subRatio;
}

interface ChordDisplayProps {
  chord: string;
  chordIndex: number;
  position: React.CSSProperties;
  fontSize: number;
  measureIndex: number;
  onChordClick: (measureIndex: number, chordIndex: number) => void;
  onChordHover: (chord: string, element: HTMLElement) => void;
  onChordLeave: () => void;
  invalidChords: Record<string, string | null>;
}

const ChordDisplay: React.FC<ChordDisplayProps> = ({ 
  chord, 
  chordIndex, 
  position, 
  fontSize,
  measureIndex,
  onChordClick,
  onChordHover,
  onChordLeave,
  invalidChords
}) => {
  // Repeat symbols use a smaller fixed ratio; regular chords use full fitted size
  const getChordFontSize = (chord: string) => {
    if (!chord) return fontSize * 0.8;
    if (chord === '%' || chord === '/' || chord === 'x') {
      return fontSize * 0.5; // repeat symbols always smaller
    }
    return fontSize; // fitting already applied upstream via getMeasureFitRatio + gigChordScale
  };

  return (
    <div 
      className="absolute cursor-pointer hover:bg-gray-600/30 rounded p-1" 
      style={position}
      data-debug-id={`Level-3:GridGigChord-${chordIndex + 1}`}
      onClick={(e) => {
        e.stopPropagation();
        onChordClick(measureIndex, chordIndex);
      }}
      onMouseEnter={(e) => {
        if (chord) onChordHover(chord, e.currentTarget as HTMLElement);
      }}
      onMouseLeave={onChordLeave}
    >
      {chord ? (
        <StyledChord 
          chord={chord || ''} 
          baseFontSize={getChordFontSize(chord)} 
          color={invalidChords[`${measureIndex}-${chordIndex}`] ? '#f87171' : '#DADEE0'}
        />
      ) : (
        <span className="font-mono select-none text-lg" style={{ color: '#DADEE0' }}>/</span>
      )}
    </div>
  );
};

const GigMeasureContent: React.FC<GigMeasureContentProps> = ({ 
  measure, 
  fontSize, 
  onChordClick, 
  onChordHover, 
  onChordLeave, 
  measureIndex, 
  invalidChords,
  gigChordScale = 1.0,
}) => {
  const chords = measure.chords;
  const subdivision = chords.length;
  const centerTransform = { transform: 'translate(-50%, -50%)' };

  // Option C : fitting dynamique — réduit selon le chord le plus long de la mesure
  // Option D : multiplicateur utilisateur
  const fitRatio = getMeasureFitRatio(chords);
  const effectiveFontSize = fontSize * fitRatio * gigChordScale;

  const chordProps = {
    fontSize: effectiveFontSize,
    measureIndex,
    onChordClick,
    onChordHover,
    onChordLeave,
    invalidChords
  };

  if (subdivision === 1) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <ChordDisplay chord={chords[0] || ''} chordIndex={0} position={{}} {...chordProps} />
      </div>
    );
  }

  if (subdivision === 2) {
    const pos1 = getIncenterPosition({ x: 0, y: 0 }, { x: 100, y: 0 }, { x: 0, y: 100 });
    const pos2 = getIncenterPosition({ x: 100, y: 0 }, { x: 100, y: 100 }, { x: 0, y: 100 });
    
    return (
      <>
        <DiagonalDivider />
        <ChordDisplay chord={chords[0] || ''} chordIndex={0} position={{ ...pos1, ...centerTransform }} {...chordProps} />
        <ChordDisplay chord={chords[1] || ''} chordIndex={1} position={{ ...pos2, ...centerTransform }} {...chordProps} />
      </>
    );
  }

  if (subdivision === 3) {
    return (
      <>
        <XDivider />
        <ChordDisplay chord={chords[0] || ''} chordIndex={0} position={{ top: '25%', left: '25%', ...centerTransform }} {...chordProps} />
        <ChordDisplay chord={chords[1] || ''} chordIndex={1} position={{ top: '25%', left: '75%', ...centerTransform }} {...chordProps} />
        <ChordDisplay chord={chords[2] || ''} chordIndex={2} position={{ top: '75%', left: '50%', ...centerTransform }} {...chordProps} />
      </>
    );
  }

  if (subdivision === 4) {
    const center = { x: 50, y: 50 };
    const tl = { x: 0, y: 0 };
    const tr = { x: 100, y: 0 };
    const bl = { x: 0, y: 100 };
    const br = { x: 100, y: 100 };

    const pos1 = getIncenterPosition(tl, bl, center);
    const pos2 = getIncenterPosition(tl, tr, center);
    const pos3 = getIncenterPosition(bl, br, center);
    const pos4 = getIncenterPosition(tr, br, center);

    return (
      <>
        <XDivider />
        <ChordDisplay chord={chords[0] || ''} chordIndex={0} position={{ ...pos1, ...centerTransform }} {...chordProps} />
        <ChordDisplay chord={chords[1] || ''} chordIndex={1} position={{ ...pos2, ...centerTransform }} {...chordProps} />
        <ChordDisplay chord={chords[2] || ''} chordIndex={2} position={{ ...pos3, ...centerTransform }} {...chordProps} />
        <ChordDisplay chord={chords[3] || ''} chordIndex={3} position={{ ...pos4, ...centerTransform }} {...chordProps} />
      </>
    );
  }

  return null; // Fallback
};

export default GigMeasureContent;
